<template>
  <div class="py-4 container-fluid">
    <div class="mt-4 row">
      <div class="col-12">
        <div class="card">
          <div class="card-header border-bottom">
            <div class="user d-flex align-items-center">
              <div class="col-12">
                <h5 class="mb-0">Phonebots</h5>
              </div>
            </div>
          </div>

          <div class="table-responsive">
            <table id="datatable-search" class="table table-flush">
              <thead class="thead-light">
                <tr>
                  <th>#</th>
                  <th>Company</th>
                  <th>Phonebot</th>
                  <th>License/UserKey</th>
                  <th>Open AI Key</th>
                  <th>Status</th>
                  <th>Verbindung</th>
                  <th>Created At</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!vbots || vbots.length === 0">
                  <td colspan="9" class="text-center">No Phonebots found.</td>
                </tr>
                <tr v-else v-for="(vbot, index) in vbots" :key="vbot.id || index">
                  <td>{{ index + 1 }}</td>
                  <td>{{ vbot.customer_name || 'N/A' }}</td>
                  <td>{{ vbot.bezeichnung || 'N/A' }}</td>
                  <td>{{ vbot.license_Key || 'N/A' }}</td>
                  <td>{{ maskApiKey(vbot.apen_ai_key) }}</td> 
                  <td>
                    <span class="badge" :class="getStatusClass(vbot.pstatus)">
                      {{ vbot.pstatus || 'N/A' }}
                    </span>
                  </td>
                  <td>
                    <span class="badge" :class="vbot.connection.badgeClass" :title="vbot.connection.tooltip">
                      {{ vbot.connection.label }}
                    </span>
                    <span v-for="warning in vbot.connection.warnings" :key="warning" class="badge bg-danger ms-1" :title="vbot.connection.warningTooltip">
                      {{ warning }}
                    </span>
                    <template v-if="vbot.connection.detail"><br><small>{{ vbot.connection.detail }}</small></template>
                  </td>
                  <td>{{ formatDate(vbot.created_at) }}</td>
                  <td>
                    <button class="btn btn-sm btn-info" @click="editItem(vbot.license_Key)" :title="'Edit Phonebot ' + vbot.bezeichnung">
                      <i class="fas fa-pen"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $ from 'jquery';
import 'datatables.net-bs5';
import 'datatables.net-bs5/css/dataTables.bootstrap5.min.css';

export default {
  name: 'Phonebots',
  data() {
    return {
      vbots: [],
      dataTable: null,
      initializationTimer: null,
    };
  },

  async mounted() {
    await this.fetchVbots();
  },

  beforeUnmount() {
    this.cleanupDataTable();
  },

  watch: {
    // Watch vbots changes für sichere DataTable updates
    vbots: {
      handler() {
        if (this.vbots && this.vbots.length > 0) {
          this.safeInitializeDataTable();
        }
      },
      deep: true
    }
  },

  methods: {
    maskApiKey(text) {
      const safeText = text || '';
      return safeText.length > 10 ? safeText.substring(0, 20) + '...' : safeText;
    },

    getStatusClass(status) {
      switch (status) {
        case 'active':
          return 'badge-success';
        case 'inactive':
          return 'badge-secondary';
        default:
          return 'badge-warning';
      }
    },

    // Spalte "Verbindung": Zustand aus dem letzten Lebenszeichen von AI-Phone (POST /api/v2/phonebot-heartbeat)
    getConnection(heartbeat) {
      if (!heartbeat) {
        return {
          label: 'keine Meldung',
          badgeClass: 'bg-secondary',
          detail: '',
          tooltip: 'AI-Phone hat noch kein Lebenszeichen gesendet (Version ohne Heartbeat oder Heartbeat nicht aktiviert).',
          warnings: [],
          warningTooltip: '',
        };
      }

      const since = this.formatDuration(heartbeat.status_since);
      const lastSeen = this.formatDuration(heartbeat.received_at);
      const states = {
        online: { label: 'angemeldet', detail: `zuletzt vor ${lastSeen}` },
        not_registered: { label: 'nicht angemeldet', detail: `seit ${since}` },
        not_running: { label: 'Telefonteil läuft nicht', detail: `seit ${since}` },
        offline: { label: 'ausgefallen', detail: `keine Meldung seit ${lastSeen}` },
      };
      const state = states[heartbeat.state] || { label: 'unbekannter Status', detail: `zuletzt vor ${lastSeen}` };

      // Rot bei Störung (ausgefallen oder über mehrere Meldungen nicht angemeldet), gelb bei einer einzelnen Abweichung
      let badgeClass = 'bg-warning text-dark';
      if (heartbeat.disturbed) {
        badgeClass = 'bg-danger';
      } else if (heartbeat.state === 'online') {
        badgeClass = 'bg-success';
      }

      // Derselbe License-Key läuft gleichzeitig mehrfach: auf mehreren Rechnern (z. B. ein alter PC läuft noch)
      // oder mit mehreren Versionen auf einem Rechner (z. B. nach einem Neustart). Das Backend liefert nur gleichzeitig laufende Installationen
      const instances = heartbeat.instances || [];
      const machineNames = [...new Set(instances.map((i) => i.name || 'unbekannt'))];
      const versionsPerMachine = machineNames.map((name) => new Set(instances.filter((i) => (i.name || 'unbekannt') === name).map((i) => i.version)).size);
      const maxVersions = Math.max(0, ...versionsPerMachine);
      const warnings = [];
      if (machineNames.length > 1) {
        warnings.push(`${machineNames.length} Rechner aktiv`);
      }
      if (maxVersions > 1) {
        warnings.push(`${maxVersions} Versionen aktiv`);
      }
      const warningTooltip = warnings.length
        ? ['Derselbe License-Key läuft gleichzeitig mehrfach:']
          .concat(instances.map((i) => `${i.name || 'unbekannt'}, Version ${i.version || '-'} (zuletzt vor ${this.formatDuration(i.last_seen)})`))
          .join('\n')
        : '';

      return { ...state, badgeClass, tooltip: this.getHeartbeatTooltip(heartbeat), warnings, warningTooltip };
    },

    // Details aus der letzten Meldung. Die Texte kommen von AI-Phone und dürfen in der DataTable nur escaped erscheinen
    getHeartbeatTooltip(heartbeat) {
      const p = heartbeat.payload || {};
      const started = p.uptimeSeconds != null
        ? this.formatDate(new Date(new Date(heartbeat.received_at).getTime() - p.uptimeSeconds * 1000))
        : '-';
      const lines = [
        `Letzte Meldung: ${this.formatDate(heartbeat.received_at)}`,
        `Rechner: ${p.machineName || '-'}${p.serviceName ? ` (${p.serviceName})` : ''}`,
        `AI-Phone-Version: ${heartbeat.app_version || '-'}`,
        `Dienst gestartet: ${started}`,
        `Letzte Anmeldung: ${this.formatDate(p.lastRegistrationAt)}`,
        `Aktive Gespräche: ${heartbeat.active_calls ?? '-'}`,
        `Fehler seit letzter Meldung: ${p.errorsSinceLastHeartbeat ?? '-'}`,
      ];
      if (p.lastError) {
        lines.push(`Letzter Fehler (${this.formatDate(p.lastErrorAt)}): ${p.lastError}`);
      }
      return lines.join('\n');
    },

    // Zeitspanne bis jetzt für "seit …" und "vor …", z. B. "3 Min.", "2 Std.", "4 Tagen"
    formatDuration(dateString) {
      const minutes = Math.floor((Date.now() - new Date(dateString).getTime()) / 60000);
      if (isNaN(minutes)) {
        return '-';
      }
      if (minutes < 1) {
        return 'weniger als 1 Min.';
      }
      if (minutes < 60) {
        return `${minutes} Min.`;
      }
      const hours = Math.floor(minutes / 60);
      return hours < 48 ? `${hours} Std.` : `${Math.floor(hours / 24)} Tagen`;
    },

    // Für die HTML-Strings der DataTable: Texte von AI-Phone (Rechnername, letzter Fehler …) nie ungeschützt einsetzen
    escapeHtml(value) {
      const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
      return String(value ?? '').replace(/[&<>"']/g, (char) => entities[char]);
    },

    async fetchVbots() {
      try {
        console.log('📡 Fetching phonebots...');
        
        const response = await this.$store.dispatch('vbot/getPhonebots');
        
        if (Array.isArray(response)) {
          // Spalte "Verbindung" einmal beim Laden berechnen (Vue-Tabelle und DataTable nutzen denselben Wert)
          this.vbots = response.map((vbot) => ({ ...vbot, connection: this.getConnection(vbot.heartbeat) }));
          console.log('✅ Successfully loaded', this.vbots.length, 'phonebots');
        } else {
          console.error('❌ Response is not an array:', response);
          this.vbots = [];
        }
        
      } catch (error) {
        console.error('❌ Error fetching phonebots:', error);
        this.vbots = [];
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '-';
      }

      try {
        const formattedDate = date.toISOString().split('T')[0];
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${formattedDate}, ${hours}:${minutes}`;
      } catch (error) {
        return '-';
      }
    },

    editItem(license_Key) {
      console.log('🔍 EditItem aufgerufen mit license_Key:', license_Key);
      this.$router.push({ 
        name: 'EditPhonebot',
        params: { license_Key: license_Key } 
      });
    },

    // ROBUSTE DataTable Initialisierung
    safeInitializeDataTable() {
      // Clear existing timer
      if (this.initializationTimer) {
        clearTimeout(this.initializationTimer);
      }

      // Warte auf DOM updates und dann initialisiere
      this.initializationTimer = setTimeout(() => {
        this.$nextTick(() => {
          this.initializeDataTable();
        });
      }, 100); // 100ms delay für sicherere DOM-State
    },

    // SAUBERE DataTable Bereinigung
    cleanupDataTable() {
      if (this.initializationTimer) {
        clearTimeout(this.initializationTimer);
        this.initializationTimer = null;
      }

      if (this.dataTable) {
        try {
          // Event handlers entfernen
          $('#datatable-search tbody').off('click', '.edit-btn');
          
          // DataTable sicher zerstören
          if ($.fn.DataTable.isDataTable('#datatable-search')) {
            this.dataTable.clear();
            this.dataTable.destroy(true); // true = remove from DOM completely
          }
          
          this.dataTable = null;
          console.log('🧹 DataTable cleaned up successfully');
        } catch (error) {
          console.warn('⚠️ Error cleaning up DataTable:', error);
          this.dataTable = null;
        }
      }
    },

    initializeDataTable() {
      // Prüfe ob DOM-Element verfügbar ist
      const tableElement = document.getElementById('datatable-search');
      if (!tableElement) {
        console.warn('⚠️ Table element not found, retrying...');
        setTimeout(() => this.safeInitializeDataTable(), 50);
        return;
      }

      // Prüfe ob Daten verfügbar sind
      if (!this.vbots || this.vbots.length === 0) {
        console.log('📊 No data available for DataTable yet');
        return;
      }

      try {
        console.log('🔧 Initializing DataTable with', this.vbots.length, 'items');
        
        // Komplett saubere Bereinigung vor Initialisierung
        this.cleanupDataTable();

        // Sicherheitscheck: Warte noch einen Tick
        this.$nextTick(() => {
          try {
            this.dataTable = $('#datatable-search').DataTable({
              destroy: true, // Automatische Bereinigung falls bereits existiert
              data: this.vbots,
              columns: [
                { 
                  data: null,
                  title: '#',
                  render: (data, type, row, meta) => meta.row + 1,
                  width: '50px',
                  orderable: false
                },
                { 
                  data: 'customer_name',
                  title: 'Company',
                  defaultContent: 'N/A'
                },
                { 
                  data: 'bezeichnung',
                  title: 'Phonebot',
                  defaultContent: 'N/A'
                },
                { 
                  data: 'license_Key',
                  title: 'License/UserKey',
                  defaultContent: 'N/A'
                },
                { 
                  data: 'apen_ai_key',
                  title: 'Open AI Key',
                  render: (data) => this.maskApiKey(data),
                  defaultContent: 'N/A'
                },
                { 
                  data: 'pstatus',
                  title: 'Status',
                  render: (data) => {
                    const statusClass = this.getStatusClass(data);
                    return `<span class="badge ${statusClass}">${data || 'N/A'}</span>`;
                  },
                  defaultContent: '<span class="badge badge-warning">N/A</span>'
                },
                {
                  data: 'connection',
                  title: 'Verbindung',
                  // Tooltip und Texte stammen teils aus dem Lebenszeichen von AI-Phone, deshalb alles escaped einsetzen
                  render: (data, type) => {
                    if (!data) {
                      return '-';
                    }
                    if (type !== 'display') {
                      return [data.label, ...data.warnings].join(' ');
                    }
                    const warnings = data.warnings
                      .map((warning) => ` <span class="badge bg-danger ms-1" title="${this.escapeHtml(data.warningTooltip)}">${this.escapeHtml(warning)}</span>`)
                      .join('');
                    const detail = data.detail ? `<br><small>${this.escapeHtml(data.detail)}</small>` : '';
                    return `<span class="badge ${data.badgeClass}" title="${this.escapeHtml(data.tooltip)}">${this.escapeHtml(data.label)}</span>${warnings}${detail}`;
                  },
                  defaultContent: '-'
                },
                {
                  data: 'created_at',
                  title: 'Created At',
                  render: (data) => this.formatDate(data),
                  defaultContent: '-'
                },
                {
                  data: null,
                  title: 'Action',
                  render: (data) => {
                    const licenseKey = data.license_Key || data.id;
                    console.log('🔍 DataTable render - Bezeichnung:', data.bezeichnung, 'license_Key:', licenseKey);
                    
                    if (!licenseKey) {
                      console.error('❌ Kein license_Key für Phonebot:', data.bezeichnung);
                      return '<span class="text-danger">Kein Key</span>';
                    }
                    
                    return `
                      <button class='btn btn-sm btn-info edit-btn' data-id='${licenseKey}' title='Edit ${data.bezeichnung || 'Phonebot'}'>
                        <i class='fas fa-pen'></i>
                      </button>
                    `;
                  },
                  orderable: false,
                  width: '80px'
                },
              ],
              order: [[0, 'asc']],
              pageLength: 25,
              responsive: true,
              autoWidth: true, // Verhindere Layout-Probleme
              language: {
                emptyTable: "Keine Phonebots verfügbar",
                info: "Zeige _START_ bis _END_ von _TOTAL_ Einträgen",
                infoEmpty: "Keine Einträge verfügbar", 
                infoFiltered: "(gefiltert von _MAX_ Gesamteinträgen)",
                lengthMenu: "Zeige _MENU_ Einträge",
                search: "Suchen:",
                zeroRecords: "Keine passenden Einträge gefunden",
                paginate: {
                  first: "‹‹",
                  last: "››",
                  next: "›",
                  previous: "‹"
                }
              },
              drawCallback: () => {
                // Sichere Border-Anwendung
                this.$nextTick(() => {
                  this.applyCustomBorders();
                });
              }
            });

            // Event handlers sicher hinzufügen
            this.$nextTick(() => {
              $('#datatable-search tbody').off('click', '.edit-btn'); // Remove old handlers first
              $('#datatable-search tbody').on('click', '.edit-btn', (event) => {
                const id = $(event.currentTarget).data('id');
                this.editItem(id);
              });
            });

            this.applyCustomBorders();
            console.log('✅ DataTable initialized successfully');
            
          } catch (error) {
            console.error('❌ Error initializing DataTable:', error);
          }
        });

      } catch (error) {
        console.error('❌ Error in DataTable initialization:', error);
      }
    },

    applyCustomBorders() {
      try {
        $('#datatable-search th, #datatable-search td').css({
          'border': '1px solid #ddd',
          'padding': '10px',
        });

        $('#datatable-search td:first-child, #datatable-search th:first-child').css({
          'border-left': '2px solid #ddd',
        });

        $('#datatable-search th').css({
          'border-top': '2px solid #ddd',
        });
      } catch (error) {
        console.warn('⚠️ Error applying borders:', error);
      }
    },
  },
};
</script>

<style scoped>
#datatable-search {
  table-layout: fixed;
  width: 100%;
}

#datatable-search td button {
  margin-right: 5px;
}

.badge-success {
  background-color: #28a745;
}

.badge-secondary {
  background-color: #6c757d;
}

.badge-warning {
  background-color: #ffc107;
  color: #000;
}
</style>